"use client"

import Link from "next/link";
import { useState } from "react";
import { FaBars, FaXmark } from "react-icons/fa6";
import Logo from "../ui/logo";
import Image from "next/image";
import camperParkeLogo from '@/components/gfx/wassersportzentrum-stellplatz-zingst.png'
import { motion, AnimatePresence } from "framer-motion";
import { captureClientEvent } from "@/lib/telemetry-client";

const menuItems = [
    { href: "/", label: "Startseite" },
    { href: "/#preise", label: "Preise & Bezahlung" },
    { href: "/#anfahrt", label: "Anfahrt" },
    { href: "/#information", label: "Informationen" },
    { href: "/#faq", label: "Häufige Fragen" },
    { href: "https://surfandwave.club", label: "Surfschule", external: true },
    { href: "/jobs", label: "Jobs" },
    { href: "/kontakt", label: "Kontakt" },
];

export default function NavMobile() {
    const [open, setOpen] = useState(false);

    return (
        <>
            {/* Fixed logo + menu button - stacked logos */}
            <motion.div 
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="fixed left-4 top-4 z-50 flex items-center md:hidden"
            >
                <Link href="/" className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 border-amber-400 bg-slate-800 shadow-lg">
                    <Image 
                        src={camperParkeLogo} 
                        alt="Stellplatz Zingst" 
                        className="h-full w-full object-cover"
                    />
                </Link>
                <Link href="/" className="-ml-6 flex h-20 w-20 items-center justify-center rounded-full border-2 border-amber-400 bg-slate-800 p-4 shadow-lg">
                    <Logo className="h-10 w-10 fill-amber-400" />
                </Link>
            </motion.div>

            {/* Menu toggle button */}
            <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                onClick={() => {
                    const nextState = !open;
                    setOpen(nextState);
                    captureClientEvent(nextState ? "mobile_menu_opened" : "mobile_menu_closed");
                }}
                className="fixed right-4 top-4 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-slate-800/90 shadow-lg backdrop-blur-sm transition-all duration-300 hover:bg-slate-700 md:hidden"
                aria-expanded={open}
                aria-label={open ? "Menü schliessen" : "Menü öffnen"}
            >
                <AnimatePresence mode="wait">
                    {open ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <FaXmark className="h-6 w-6 text-white" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="menu"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <FaBars className="h-6 w-6 text-white" />
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Backdrop */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-40 bg-slate-900/60 backdrop-blur-sm md:hidden"
                        onClick={() => setOpen(false)}
                    />
                )}
            </AnimatePresence>

            {/* Slide-out menu */}
            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="fixed bottom-0 right-0 top-0 z-50 w-full max-w-sm overflow-y-auto bg-gradient-to-br from-slate-800 to-slate-900 px-6 py-8 shadow-2xl md:hidden"
                    >
                        {/* Header */}
                        <div className="mb-10 flex items-center gap-4 pt-4">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-700/50">
                                <Logo className="h-10 w-10 fill-amber-400" />
                            </div>
                            <div>
                                <h2 className="font-special text-xl font-bold text-white">
                                    Stellplatz Zingst
                                </h2>
                                <p className="font-handwritten text-lg text-amber-300">
                                    Direkt an der Ostsee
                                </p>
                            </div>
                        </div>

                        {/* Menu items */}
                        <nav className="space-y-2">
                            {menuItems.map((item, index) => (
                                <motion.div
                                    key={item.href}
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ duration: 0.3, delay: 0.1 + index * 0.05 }}
                                >
                                    {item.external ? (
                                        <a
                                            href={item.href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            onClick={() => {
                                                setOpen(false);
                                                captureClientEvent("navigation_clicked", { section: "mobile_nav", target: item.label, external: true });
                                            }}
                                            className="group flex items-center gap-4 rounded-xl px-4 py-4 transition-all duration-300 hover:bg-white/10"
                                        >
                                            <div className="h-2 w-2 rounded-full bg-amber-400 transition-transform duration-300 group-hover:scale-150" />
                                            <span className="text-lg font-medium text-white/90 transition-colors group-hover:text-white">
                                                {item.label}
                                            </span>
                                        </a>
                                    ) : (
                                        <Link
                                            href={item.href}
                                            onClick={() => {
                                                setOpen(false);
                                                captureClientEvent("navigation_clicked", { section: "mobile_nav", target: item.label, external: false });
                                            }}
                                            className="group flex items-center gap-4 rounded-xl px-4 py-4 transition-all duration-300 hover:bg-white/10"
                                        >
                                            <div className="h-2 w-2 rounded-full bg-amber-400 transition-transform duration-300 group-hover:scale-150" />
                                            <span className="text-lg font-medium text-white/90 transition-colors group-hover:text-white">
                                                {item.label}
                                            </span>
                                        </Link>
                                    )}
                                </motion.div>
                            ))}
                        </nav>

                        {/* CTA Button */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4, delay: 0.5 }}
                            className="mt-8"
                        >
                            <Link
                                href="/#preise"
                                onClick={() => {
                                    setOpen(false);
                                    captureClientEvent("navigation_clicked", { section: "mobile_nav", target: "ticket_kaufen" });
                                }}
                            >
                                <button className="w-full rounded-full bg-gradient-to-r from-amber-400 to-amber-500 py-4 font-semibold text-slate-900 shadow-lg shadow-amber-500/20 transition-all duration-300 hover:shadow-xl">
                                    Jetzt Ticket kaufen
                                </button>
                            </Link>
                        </motion.div>

                        {/* Close button inside */}
                        <motion.button
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.6 }}
                            onClick={() => setOpen(false)}
                            className="mt-6 w-full rounded-xl border border-white/20 py-3 text-sm font-medium text-white/60 transition-colors hover:bg-white/5 hover:text-white"
                        >
                            Menü schliessen
                        </motion.button>

                        {/* X close button top right */}
                        <motion.button
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2 }}
                            onClick={() => {
                                setOpen(false);
                                captureClientEvent("mobile_menu_closed", { source: "internal_close_button" });
                            }}
                            className="absolute right-4 top-4 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20"
                            aria-label="Menü schliessen"
                        >
                            <FaXmark className="h-5 w-5" />
                        </motion.button>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}
